/* Sales · Doctors — prescribing-doctor master.
 * List + search + a slide-in drawer to add or edit a doctor.
 * Reuses supplyService.getDoctors / createDoctor / updateDoctor
 * (GET / POST / PUT v1/doctors). Mirrors the Suppliers master (Purchase).
 * The DoctorDrawer is exported so Sales Bill Entry can add a doctor inline. */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import supplyService from "../../Services/supplyService";
import { getResolvedWarehouseCode } from "../../Services/warehouseContext";
import { useWarehouse } from "../Layout/WarehouseContext";
import {
  Button,
  Pill,
  Input,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  Eyebrow,
  LoadingState,
  ErrorState,
  InlineAlert,
  IconPlus,
  IconX,
  IconCheck,
  IconChevRight,
  IconUser,
  emailError,
  phoneError,
} from "./supplyKit";
import { WarehouseSelect } from "./warehousePicker";

const fieldInput = {
  width: "100%",
  height: 30,
  padding: "0 8px",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  background: "var(--bg-panel)",
  color: "var(--fg-default)",
  outline: "none",
};
const fieldLabel = {
  display: "block",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  marginBottom: 4,
};

const Field = ({ label, children }) => (
  <div>
    <label style={fieldLabel}>{label}</label>
    {children}
  </div>
);

export const blankDoctor = () => ({
  /* Required — defaults to the logged-in warehouse; admin can re-assign. */
  warehouseId: getResolvedWarehouseCode() || "",
  name: "",
  registrationNo: "",
  qualification: "",
  speciality: "",
  phone: "",
  email: "",
  isDefault: false,
  isActive: true,
});

const Doctors = () => {
  /* Scoped to the store selected app-wide (topbar), for every role — a head
     switches store there rather than seeing every store's doctors at once. */
  const { code: wh } = useWarehouse();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [doctors, setDoctors] = useState([]);
  const [q, setQ] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    /* Only this store's doctors — see the app-wide selection above. */
    supplyService
      .getDoctors({
        warehouseId: wh || undefined,
      })
      .then((list) => {
        if (!alive) return;
        setDoctors(list || []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load doctors.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [reloadKey, wh]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return doctors;
    return doctors.filter((d) =>
      [d.name, d.registrationNo, d.qualification, d.speciality, d.phone, d.email]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [doctors, q]);

  const openNew = () => {
    setEditing(null);
    setShowDrawer(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setShowDrawer(true);
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Doctors"
        sub="Prescribing-doctor master · used when creating sales bills"
        actions={
          <Button variant="primary" icon={<IconPlus size={14} />} onClick={openNew}>
            New doctor
          </Button>
        }
      />

      <ToolbarBar>
        <Input
          placeholder="Search name / reg. no / speciality / phone…"
          style={{ width: 320, maxWidth: "100%" }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {rows.length} doctor{rows.length !== 1 ? "s" : ""}
        </div>
      </ToolbarBar>

      {loading ? (
        <LoadingState label="Loading doctors…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <Table
          columns={[
            { label: "Doctor" },
            { label: "Reg. no" },
            { label: "Speciality" },
            { label: "Phone" },
            { label: "Default" },
            { label: "Status" },
            { label: "", width: 24 },
          ]}
        >
          {rows.map((d) => (
            <Tr key={d.id} onClick={() => openEdit(d)}>
              <Td style={{ fontWeight: 600, color: "var(--fg-strong)" }}>
                {d.name}
                {d.qualification && (
                  <div style={{ fontSize: 10, color: "var(--fg-muted)" }}>{d.qualification}</div>
                )}
              </Td>
              <Td mono>{d.registrationNo || "—"}</Td>
              <Td>{d.speciality || "—"}</Td>
              <Td mono>{d.phone || "—"}</Td>
              <Td>{d.isDefault ? <Pill variant="green" dot>default</Pill> : "—"}</Td>
              <Td>
                <Pill variant={d.isActive === false ? "neutral" : "green"} dot>
                  {d.isActive === false ? "inactive" : "active"}
                </Pill>
              </Td>
              <Td>
                <IconChevRight size={12} style={{ color: "var(--fg-muted)" }} />
              </Td>
            </Tr>
          ))}
          {rows.length === 0 && (
            <Tr>
              <Td colSpan={7} style={{ textAlign: "center", color: "var(--fg-muted)", padding: 20 }}>
                No doctors{q ? " match your search" : " yet"}.
              </Td>
            </Tr>
          )}
        </Table>
      )}

      {showDrawer && (
        <DoctorDrawer
          editing={editing}
          onClose={() => {
            setShowDrawer(false);
            setEditing(null);
          }}
          onSaved={() => {
            setReloadKey((k) => k + 1);
            setShowDrawer(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

/* ---------------- Add / edit drawer ----------------
   Exported so Sales Bill Entry can open it for an inline "Add doctor". On save
   it calls back with the created/updated doctor so the caller can select it. */
export const DoctorDrawer = ({ editing, onClose, onSaved }) => {
  const isEditing = Boolean(editing?.id);
  const [form, setForm] = useState(() => (isEditing ? { ...blankDoctor(), ...editing } : blankDoctor()));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [warehouses, setWarehouses] = useState([]);

  const setF = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  /* Warehouse options for the required scoping selector. */
  useEffect(() => {
    let alive = true;
    supplyService.getWarehouses().then((list) => {
      if (alive) setWarehouses(list || []);
    });
    return () => {
      alive = false;
    };
  }, []);

  const phoneErr = phoneError(form.phone);
  const emailErr = emailError(form.email);

  const valid = form.name.trim().length > 0 && !!form.warehouseId && !phoneErr && !emailErr;

  const handleSave = () => {
    if (!valid) return;
    setSaving(true);
    setErr(null);
    const call = isEditing
      ? supplyService.updateDoctor(editing.id, form)
      : supplyService.createDoctor(form);
    call
      .then((saved) => {
        toast.success(`Doctor ${isEditing ? "updated" : "added"} — ${form.name}.`);
        onSaved?.(saved);
      })
      .catch((e) => {
        setSaving(false);
        setErr(e?.response?.data?.message || e?.message || "Could not save the doctor.");
      });
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 70 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "var(--bg-overlay)" }} />
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(520px, 96vw)",
          background: "var(--bg-canvas)",
          boxShadow: "-12px 0 40px rgba(13,17,27,0.18)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        className="cb-animate-in"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 20px",
            borderBottom: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              background: "var(--ink-50)",
              color: "var(--ink-700)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconUser size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fg-strong)" }}>
              {isEditing ? `Edit ${editing.name}` : "New doctor"}
            </div>
            <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 2 }}>Prescribing-doctor record</div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: 0,
              background: "transparent",
              cursor: "pointer",
              color: "var(--fg-muted)",
              width: 28,
              height: 28,
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconX size={16} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "16px 20px" }} className="cb-scroll">
          <Eyebrow style={{ display: "block", marginBottom: 10 }}>Doctor details</Eyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2" style={{ display: "grid", gap: 12 }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Warehouse *">
                {/* Defaults to the logged-in user's warehouse. Only a pharmacy
                    head (admin) sees other warehouses and can reassign; a plain
                    PH user sees ONLY their own warehouse and is locked to it. */}
                {/* Searchable for a head (10+ similarly-named warehouses); a
                    static label for a PH user, who has only their own. */}
                <WarehouseSelect
                  value={form.warehouseId || ""}
                  onChange={(v) => setF("warehouseId", v)}
                  warehouses={warehouses}
                  noneLabel="— Select warehouse —"
                  /* Always the store in scope: records are created in the store
                     you are working in, which the topbar names. To file one under
                     another store, switch store there. */
                  disabled
                  width="100%"
                />
              </Field>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Name *">
                <input
                  value={form.name}
                  onChange={(e) => setF("name", e.target.value)}
                  placeholder="Dr. Ramesh Kumar"
                  style={fieldInput}
                />
              </Field>
            </div>
            <Field label="Registration no">
              <input
                value={form.registrationNo}
                onChange={(e) => setF("registrationNo", e.target.value)}
                placeholder="Medical council reg. no"
                style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
              />
            </Field>
            <Field label="Qualification">
              <input
                value={form.qualification}
                onChange={(e) => setF("qualification", e.target.value)}
                placeholder="MBBS, MD"
                style={fieldInput}
              />
            </Field>
            <Field label="Speciality">
              <input
                value={form.speciality}
                onChange={(e) => setF("speciality", e.target.value)}
                placeholder="General Physician"
                style={fieldInput}
              />
            </Field>
            <Field label="Phone">
              <input
                value={form.phone}
                onChange={(e) => setF("phone", e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
                inputMode="numeric"
                maxLength={10}
                placeholder="9876543210"
                style={{
                  ...fieldInput,
                  fontFamily: "var(--font-mono)",
                  border: phoneErr ? "1px solid var(--red-500, #d92d20)" : fieldInput.border,
                }}
              />
              {phoneErr && (
                <div style={{ fontSize: 10, color: "var(--red-700, #b42318)", marginTop: 4 }}>{phoneErr}</div>
              )}
            </Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Email">
                <input
                  value={form.email}
                  onChange={(e) => setF("email", e.target.value)}
                  type="email"
                  placeholder="doctor@clinic.in"
                  style={{
                    ...fieldInput,
                    border: emailErr ? "1px solid var(--red-500, #d92d20)" : fieldInput.border,
                  }}
                />
                {emailErr && (
                  <div style={{ fontSize: 10, color: "var(--red-700, #b42318)", marginTop: 4 }}>{emailErr}</div>
                )}
              </Field>
            </div>
            <Field label="Default doctor">
              {/* The default is pre-selected when creating a sales bill. */}
              <select
                value={form.isDefault ? "yes" : "no"}
                onChange={(e) => setF("isDefault", e.target.value === "yes")}
                style={fieldInput}
              >
                <option value="no">No</option>
                <option value="yes">Yes — default for billing</option>
              </select>
            </Field>
            <Field label="Status">
              <select
                value={form.isActive === false ? "inactive" : "active"}
                onChange={(e) => setF("isActive", e.target.value === "active")}
                style={fieldInput}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>
          </div>

          {err && (
            <div style={{ marginTop: 12 }}>
              <InlineAlert>{err}</InlineAlert>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            padding: "12px 20px",
            borderTop: "1px solid var(--border-default)",
            background: "var(--bg-panel)",
          }}
        >
          <Button onClick={onClose}>Cancel</Button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: valid ? "var(--green-700)" : "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
            {valid ? "Ready to save" : !form.warehouseId ? "Warehouse required" : "Doctor name required"}
          </span>
          <Button variant="primary" icon={<IconCheck size={14} />} disabled={!valid || saving} onClick={handleSave}>
            {saving ? "Saving…" : isEditing ? "Save changes" : "Add doctor"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Doctors;
